import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ImageList,
  ImageListItem,
  Typography,
  CircularProgress,
  Tabs,
  Tab,
  IconButton,
  Alert,
  TextField,
  FormControlLabel,
  Switch,
  Divider
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
import {
  getImages,
  IMAGE_CATEGORIES,
  ImageCategory,
  ImageMetadata,
  uploadImage
} from '../../firebase/services/imageService';

interface ImageSelectorDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (imageUrl: string) => void;
  title?: string;
  defaultCategory?: ImageCategory;
  singleCategory?: boolean;
  allowUpload?: boolean;
}

const ImageSelectorDialog: React.FC<ImageSelectorDialogProps> = ({
  open,
  onClose,
  onSelect,
  title = 'Select Image',
  defaultCategory = 'MARINA',
  singleCategory = false,
  allowUpload = false
}) => {
  const [selectedCategory, setSelectedCategory] = useState<ImageCategory>(defaultCategory);
  const [images, setImages] = useState<ImageMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<ImageMetadata | null>(null);
  const [uploadMode, setUploadMode] = useState(false);
  
  // Upload form state
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [imageTitle, setImageTitle] = useState('');
  const [imageAltText, setImageAltText] = useState('');
  const [uploadProgress, setUploadProgress] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      loadImages();
      resetUploadForm();
    } else {
      setSelectedImage(null);
      setUploadMode(false);
    }
  }, [open, selectedCategory]);

  useEffect(() => {
    setSelectedCategory(defaultCategory);
  }, [defaultCategory]);

  const loadImages = async () => {
    try {
      setLoading(true);
      setError(null);
      const fetchedImages = await getImages(selectedCategory);
      setImages(fetchedImages);
    } catch (error: any) {
      console.error('Failed to load images:', error);
      setError('Failed to load images. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (_event: React.SyntheticEvent, newValue: ImageCategory) => {
    setSelectedCategory(newValue);
  };

  const handleImageSelect = (image: ImageMetadata) => {
    setSelectedImage(image);
  };

  const handleConfirmSelection = () => {
    if (selectedImage) {
      onSelect(selectedImage.storageUrl);
      onClose();
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    setUploadFile(files[0]);
  };

  const handleUploadImage = async () => {
    if (!uploadFile) {
      setError('Please select a file to upload');
      return;
    }

    try {
      setUploadProgress(true);
      setError(null);

      const metadata: Partial<ImageMetadata> = {
        title: imageTitle || uploadFile.name,
        altText: imageAltText || imageTitle || uploadFile.name,
        isPublic: true // Always set to true
      };

      await uploadImage(uploadFile, selectedCategory, metadata);
      setSuccess('Image uploaded successfully');
      
      // Reset form and reload images
      resetUploadForm();
      loadImages();
      setUploadMode(false);
    } catch (error: any) {
      setError(error.message || 'Failed to upload image');
    } finally {
      setUploadProgress(false);
    }
  };

  const resetUploadForm = () => {
    setUploadFile(null);
    setImageTitle('');
    setImageAltText('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const categories = Object.keys(IMAGE_CATEGORIES) as Array<keyof typeof IMAGE_CATEGORIES>;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      aria-labelledby="image-selector-dialog-title"
    >
      <DialogTitle id="image-selector-dialog-title">
        <Box display="flex" alignItems="center" justifyContent="space-between">
          {title}
          <IconButton edge="end" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {!singleCategory && (
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs 
              value={selectedCategory} 
              onChange={handleCategoryChange}
              variant="scrollable"
              scrollButtons="auto"
            >
              {categories.map((category) => (
                <Tab 
                  key={category} 
                  label={category.charAt(0) + category.slice(1).toLowerCase().replace('_', ' ')} 
                  value={category}
                />
              ))}
            </Tabs>
          </Box>
        )}

        {allowUpload && (
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle1">
              {!uploadMode ? 'Select an image from the gallery' : 'Upload a new image'}
            </Typography>
            <Button 
              variant="outlined" 
              startIcon={uploadMode ? <CheckCircleIcon /> : <CloudUploadIcon />} 
              onClick={() => setUploadMode(!uploadMode)}
            >
              {uploadMode ? 'Back to Gallery' : 'Upload New Image'}
            </Button>
          </Box>
        )}

        {uploadMode && allowUpload ? (
          <Box sx={{ p: 2, border: '1px dashed #ccc', borderRadius: 1, mb: 2 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            
            <Box sx={{ mb: 3, textAlign: 'center' }}>
              <Button
                variant="contained"
                startIcon={<CloudUploadIcon />}
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadProgress}
              >
                Select Image File
              </Button>
              
              {uploadFile && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Selected: {uploadFile.name} ({Math.round(uploadFile.size / 1024)} KB)
                </Typography>
              )}
            </Box>
            
            <TextField
              fullWidth
              label="Title"
              value={imageTitle}
              onChange={(e) => setImageTitle(e.target.value)}
              margin="normal"
              size="small"
              placeholder={uploadFile ? uploadFile.name : 'Image title'}
            />
            
            <TextField
              fullWidth
              label="Alt Text (for accessibility)"
              value={imageAltText}
              onChange={(e) => setImageAltText(e.target.value)}
              margin="normal"
              size="small"
              placeholder="Describe the image for screen readers"
              helperText="Text that will be read by screen readers and used if the image fails to load"
            />
            
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button 
                variant="contained" 
                onClick={handleUploadImage}
                disabled={!uploadFile || uploadProgress}
              >
                {uploadProgress ? <CircularProgress size={24} /> : 'Upload Image'}
              </Button>
            </Box>
          </Box>
        ) : (
          loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
              <CircularProgress />
            </Box>
          ) : images.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px" flexDirection="column">
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                No images found in this category
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center" sx={{ maxWidth: 400 }}>
                {allowUpload 
                  ? 'Click the "Upload New Image" button to add images to this category.'
                  : 'To add new images, please go to the Image Management page in the admin dashboard and upload files to the appropriate category.'
                }
              </Typography>
            </Box>
          ) : (
            <ImageList cols={3} gap={16} sx={{ maxHeight: 500 }}>
              {images.map((image) => (
                <ImageListItem 
                  key={image.id}
                  onClick={() => handleImageSelect(image)}
                  sx={{ 
                    cursor: 'pointer',
                    position: 'relative',
                    border: selectedImage?.id === image.id ? '2px solid #1976d2' : 'none',
                    borderRadius: 1,
                    overflow: 'hidden'
                  }}
                >
                  <img
                    src={image.storageUrl}
                    alt={image.altText || image.title || image.filename}
                    loading="lazy"
                    style={{ height: 160, objectFit: 'cover' }}
                  />
                  <Box sx={{ 
                    position: 'absolute', 
                    bottom: 0, 
                    left: 0, 
                    right: 0, 
                    bgcolor: 'rgba(0,0,0,0.6)', 
                    color: 'white',
                    p: 1,
                    fontSize: '0.8rem',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {image.title || image.filename}
                  </Box>
                  {selectedImage?.id === image.id && (
                    <Box 
                      sx={{ 
                        position: 'absolute', 
                        top: 8, 
                        right: 8, 
                        bgcolor: 'primary.main',
                        borderRadius: '50%'
                      }}
                    >
                      <CheckCircleIcon color="inherit" sx={{ color: 'white' }} />
                    </Box>
                  )}
                </ImageListItem>
              ))}
            </ImageList>
          )
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {!uploadMode && (
          <Button 
            onClick={handleConfirmSelection} 
            variant="contained" 
            disabled={!selectedImage}
          >
            Select
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImageSelectorDialog; 